/**
 * Behavioural tracking SDK (anonymous-first).
 *
 * Records what customers do *before* they order — page/product views,
 * impressions, searches, add-to-cart, checkout steps, purchases — and beacons
 * them to POST /api/track in small batches.
 *
 * Identity:
 *   • visitorId — random id persisted in localStorage (one per browser)
 *   • sessionId — random id in sessionStorage, rotated after 30 min idle
 *   • customerId — attached only after identify() is called (i.e. when signed in)
 *
 * Delivery: events are queued and flushed on a short timer, when the batch
 * fills, and on page hide (via sendBeacon so in-flight data isn't lost on
 * navigation/close). Tracking never throws into the app.
 */
import { API } from '../api/baseURL';

const VID_KEY  = 'nooshin_vid';
const SID_KEY  = 'nooshin_sid';
const SID_TS   = 'nooshin_sid_ts';
const SESSION_IDLE_MS = 30 * 60 * 1000;   // 30 min
const FLUSH_MS = 4000;
const BATCH_MAX = 20;

let queue = [];
let timer = null;
let customerId = null;

function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function safeGet(store, key) { try { return store.getItem(key); } catch { return null; } }
function safeSet(store, key, val) { try { store.setItem(key, val); } catch { /* ignore */ } }

function _getVisitorId() {
  let id = safeGet(localStorage, VID_KEY);
  if (!id) { id = uuid(); safeSet(localStorage, VID_KEY, id); }
  return id;
}

function getSessionId() {
  const now = Date.now();
  const last = Number(safeGet(sessionStorage, SID_TS) || 0);
  let id = safeGet(sessionStorage, SID_KEY);
  if (!id || (now - last) > SESSION_IDLE_MS) {
    id = uuid();
    safeSet(sessionStorage, SID_KEY, id);
  }
  safeSet(sessionStorage, SID_TS, String(now));
  return id;
}

// Current restaurant slug from the subdomain (mirrors apiClient logic).
function currentSlug() {
  try {
    const parts = window.location.hostname.toLowerCase().split('.');
    const SKIP = new Set(['www', 'api', 'app', 'mail', 'ftp', 'localhost']);
    if (parts.length === 2 && parts[1] === 'localhost' && !SKIP.has(parts[0])) return parts[0];
    if (parts.length >= 3 && !SKIP.has(parts[0]) && !/^\d+$/.test(parts[0])) return parts[0];
  } catch { /* ignore */ }
  return null;
}

function flush(useBeacon = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  if (queue.length === 0) return;
  const slug = currentSlug();
  if (!slug) { queue = []; return; }

  const payload = {
    slug,
    visitorId: _getVisitorId(),
    sessionId: getSessionId(),
    customerId: customerId || undefined,
    referrer: document.referrer || '',
    events: queue,
  };
  queue = [];

  const url = `${API}/track`;
  try {
    const body = JSON.stringify(payload);
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
        .catch(() => {});
    }
  } catch { /* never throw from analytics */ }
}

function scheduleFlush() {
  if (queue.length >= BATCH_MAX) { flush(); return; }
  if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
}

/** The anonymous visitor id (for personalised recommendation requests). */
export function getVisitorId() { return _getVisitorId(); }

/** Attach the signed-in customer's id to subsequent events (or clear with null). */
export function identify(id) {
  customerId = (typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id)) ? id : null;
}

/** Queue a single event. `props` carries the type-specific fields. */
export function track(type, props = {}) {
  if (!type) return;
  try {
    queue.push({
      type,
      productId:    props.productId,
      productName:  props.productName,
      categorySlug: props.categorySlug,
      query:        props.query,
      value:        props.value,
      quantity:     props.quantity,
      orderId:      props.orderId,
      lang:         props.lang,
      path:         (typeof window !== 'undefined') ? window.location.pathname : '',
      meta:         props.meta,
      ts:           Date.now(),
    });
    scheduleFlush();
  } catch { /* ignore */ }
}

// ── Convenience helpers ───────────────────────────────────────────────────────
export const trackPageView      = (props = {})        => track('page_view', props);
export const trackProductView   = (product, props={}) => track('product_view', { productId: product?._id || product?.id, productName: product?.name, value: product?.price, categorySlug: product?.categorySlug, ...props });
export const trackCategoryView  = (categorySlug)      => track('category_view', { categorySlug });
export const trackSearch        = (query)             => { if (query && query.trim().length >= 2) track('search', { query: query.trim() }); };
export const trackAddToCart     = (product, qty=1)    => track('add_to_cart', { productId: product?._id || product?.id, productName: product?.name, value: product?.price, quantity: qty });
export const trackRemoveFromCart= (product, qty=1)    => track('remove_from_cart', { productId: product?._id || product?.id, productName: product?.name, quantity: qty });
export const trackViewCart      = (value=0, qty=0)    => track('view_cart', { value, quantity: qty });
export const trackBeginCheckout = (value=0, qty=0)    => track('begin_checkout', { value, quantity: qty });
export const trackPurchase      = (order)             => track('purchase', { orderId: order?._id || order?.orderNumber, value: order?.total, quantity: (order?.items || []).reduce((s, i) => s + (i.quantity || 0), 0) });

// Impressions: a list shown to the user. Debounced/deduped by caller.
export function trackImpressions(products = [], categorySlug) {
  (products || []).slice(0, 30).forEach(p =>
    track('product_impression', { productId: p?._id || p?.id, productName: p?.name, value: p?.price, categorySlug }));
}

// Flush on page hide so navigations/closes don't lose the tail of the queue.
if (typeof window !== 'undefined') {
  const onHide = () => flush(true);
  window.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') onHide(); });
  window.addEventListener('pagehide', onHide);
}
